import { render, button, playerCard } from './components';
import { GameManager } from '../game';
import { PlayerState } from '../types';

export interface ResultCallbacks {
  onNext: () => void;
  onRestart?: () => void;
}

// ラウンド終了画面
export function renderRoundResult(
  game: GameManager,
  roundNumber: number,
  callbacks: ResultCallbacks
): void {
  const ranking = game.getRanking();
  const totalRounds = game.getState().config.qualifyingRounds;
  const gamePhase = game.getState().phase;
  const isFinalRound = gamePhase === 'finals';
  const activeCount = ranking.filter((r) => !r.player.isEliminated).length;

  render(`
    <div class="round-result" style="padding: 30px 16px; max-width: 600px; margin: 0 auto; text-align: center;">
      <h1 style="
        color: #00fff5;
        text-shadow: 0 0 20px #00fff5;
        margin-bottom: 8px;
        font-size: clamp(1.5rem, 5vw, 2rem);
      ">Round ${roundNumber} 終了</h1>
      
      <p style="color: #aaa; margin-bottom: 24px; font-size: 0.9rem;">
        ${isFinalRound 
          ? '予選終了! 決勝進出者が決定!' 
          : `残り ${totalRounds - roundNumber} ラウンド | アクティブ ${activeCount}人`}
      </p>

      <div style="
        background: rgba(0,0,0,0.3);
        border-radius: 12px;
        padding: 16px;
        margin-bottom: 24px;
        border: 1px solid rgba(0,255,245,0.2);
      ">
        <h2 style="color: #ffd700; margin-bottom: 14px; font-size: 1.1rem;">📊 現在のランキング</h2>
        
        ${ranking.map((r, i) => {
          const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '';
          const isFinalist = i < 2 && !r.player.isEliminated;
          const isEliminated = r.player.isEliminated;
          
          return `
            <div style="
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 10px 12px;
              margin: 6px 0;
              background: ${isEliminated ? 'rgba(100,100,100,0.2)' : 'rgba(255,255,255,0.05)'};
              border-radius: 8px;
              ${isFinalist ? 'border: 1px solid #ffd700;' : ''}
              ${isEliminated ? 'opacity: 0.5;' : ''}
            ">
              <div style="display: flex; align-items: center; gap: 10px;">
                <span style="font-size: 1rem; width: 28px;">${medal || `#${r.rank}`}</span>
                <span>${r.player.info.type === 'bot' ? '🤖' : '👤'}</span>
                <span style="font-weight: bold; font-size: 0.9rem;">${r.player.info.name}</span>
                ${isEliminated ? `<span style="color: #e74c3c; font-size: 0.7rem; background: rgba(231,76,60,0.2); padding: 2px 6px; border-radius: 4px;">敗退</span>` : ''}
              </div>
              <div style="text-align: right;">
                <span style="color: #ffd700; font-weight: bold;">★ ${r.player.fans}</span>
                ${r.status && !isEliminated ? `<div style="font-size: 0.65rem; color: #888;">${r.player.matchHistory.filter(m => m.result === 'win').length}勝</div>` : ''}
              </div>
            </div>
          `;
        }).join('')}
      </div>

      ${isFinalRound 
        ? `<p style="color: #ffd700; margin-bottom: 16px; font-size: 0.95rem;">🏆 上位2名が決勝進出!</p>`
        : ''
      }

      <button id="btnNext" class="btn" style="
        min-height: 48px;
        padding: 12px 32px;
        font-size: 1rem;
      ">${isFinalRound ? '🏆 決勝戦へ' : '➡️ 次のラウンドへ'}</button>
    </div>
  `);

  document.getElementById('btnNext')?.addEventListener('click', callbacks.onNext);
}

// 決勝戦開始画面
export function renderFinalsIntro(
  finalist1: PlayerState,
  finalist2: PlayerState,
  onStart: () => void
): void {
  render(`
    <div class="finals-intro" style="padding: 40px 20px; text-align: center;">
      <h1 style="
        color: #ffd700;
        text-shadow: 0 0 40px #ffd700;
        font-size: 3rem;
        margin-bottom: 30px;
        animation: pulse 1.5s ease-in-out infinite;
      ">🏆 FINALS 🏆</h1>

      <div style="
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 40px;
        margin: 40px 0;
      ">
        <div style="text-align: center;">
          <div style="font-size: 3rem; margin-bottom: 8px;">
            ${finalist1.info.type === 'bot' ? '🤖' : '👤'}
          </div>
          <div style="font-size: 1.5rem; font-weight: bold; color: #fff;">
            ${finalist1.info.name}
          </div>
          <div style="color: #ffd700;">★ ${finalist1.fans}</div>
        </div>

        <div style="
          font-size: 2.5rem;
          color: #ff6b6b;
          text-shadow: 0 0 20px #ff6b6b;
        ">VS</div>

        <div style="text-align: center;">
          <div style="font-size: 3rem; margin-bottom: 8px;">
            ${finalist2.info.type === 'bot' ? '🤖' : '👤'}
          </div>
          <div style="font-size: 1.5rem; font-weight: bold; color: #fff;">
            ${finalist2.info.name}
          </div>
          <div style="color: #ffd700;">★ ${finalist2.fans}</div>
        </div>
      </div>

      ${button('⚔️ FIGHT!', 'btnStartFinals', 'primary')}
    </div>
  `);

  document.getElementById('btnStartFinals')?.addEventListener('click', onStart);
}

// ゲーム終了画面
export function renderGameEnd(
  champion: PlayerState,
  ranking: { rank: number; player: PlayerState; status: string }[],
  callbacks: ResultCallbacks
): void {
  const isChampionBot = champion.info.type === 'bot';

  render(`
    <div class="game-end" style="padding: 30px 16px; text-align: center;">
      <div style="animation: fadeIn 1s ease-out;">
        <h1 style="
          color: #ffd700;
          text-shadow: 0 0 50px #ffd700;
          font-size: clamp(2rem, 8vw, 3.5rem);
          margin-bottom: 16px;
        ">🏆 CHAMPION 🏆</h1>

        <div style="font-size: clamp(3rem, 10vw, 5rem); margin: 16px 0;">
          ${isChampionBot ? '🤖' : '👤'}
        </div>

        <div style="
          font-size: clamp(1.3rem, 5vw, 2rem);
          font-weight: bold;
          color: #fff;
          margin-bottom: 8px;
        ">${champion.info.name}</div>

        <div style="
          color: #ffd700;
          font-size: clamp(1rem, 4vw, 1.5rem);
        ">★ ${champion.fans} Fans</div>
      </div>

      <div style="
        background: rgba(0,0,0,0.3);
        border-radius: 12px;
        padding: 16px;
        margin: 30px auto;
        max-width: 500px;
        border: 1px solid rgba(255,215,0,0.3);
      ">
        <h2 style="color: #aaa; margin-bottom: 14px; font-size: 1rem;">Final Ranking</h2>
        
        ${ranking.map((r) => {
          const medal = r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : '';
          const isEliminated = r.player.isEliminated;
          
          return `
            <div style="
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 8px 10px;
              margin: 5px 0;
              background: ${isEliminated ? 'rgba(100,100,100,0.15)' : 'rgba(255,255,255,0.03)'};
              border-radius: 6px;
              ${isEliminated ? 'opacity: 0.6;' : ''}
            ">
              <div style="display: flex; align-items: center; gap: 8px; font-size: 0.9rem;">
                <span style="width: 22px;">${medal || `#${r.rank}`}</span>
                <span>${r.player.info.type === 'bot' ? '🤖' : '👤'}</span>
                <span>${r.player.info.name}</span>
                ${isEliminated ? `<span style="color: #888; font-size: 0.65rem;">(${r.status})</span>` : ''}
              </div>
              <span style="color: #ffd700; font-size: 0.9rem;">★ ${r.player.fans}</span>
            </div>
          `;
        }).join('')}
      </div>

      <button id="btnRestart" class="btn" style="
        min-height: 48px;
        padding: 12px 32px;
        font-size: 1rem;
      ">🔄 もう一度遊ぶ</button>
    </div>
  `);

  document.getElementById('btnRestart')?.addEventListener('click', () => {
    callbacks.onRestart?.();
  });
}

// アニメーション用CSS
const resultStyle = document.createElement('style');
resultStyle.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
  }
`;
document.head.appendChild(resultStyle);

// 敗退画面
export function renderEliminationScreen(
  player: PlayerState,
  finalRank: number,
  totalPlayers: number,
  callbacks: { onSpectate: () => void; onExit: () => void }
): void {
  const wins = player.matchHistory.filter((m) => m.result === 'win').length;
  const losses = player.matchHistory.filter((m) => m.result === 'lose').length;

  render(`
    <div class="elimination-screen" style="
      padding: 30px 16px;
      text-align: center;
      min-height: 100vh;
      min-height: 100dvh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    ">
      <div style="animation: shake 0.5s ease-out;">
        <div style="
          font-size: clamp(3rem, 12vw, 5rem);
          margin-bottom: 16px;
        ">💀</div>
        
        <h1 style="
          color: #e74c3c;
          font-size: clamp(1.5rem, 6vw, 2.5rem);
          margin-bottom: 8px;
          text-shadow: 0 0 20px rgba(231,76,60,0.5);
        ">ELIMINATED</h1>
        
        <p style="color: #888; font-size: 1rem; margin-bottom: 24px;">
          Round ${player.eliminatedRound ?? '?'} で敗退
        </p>
      </div>

      <!-- スタッツ -->
      <div style="
        background: rgba(0,0,0,0.4);
        border-radius: 16px;
        padding: 20px;
        max-width: 350px;
        width: 100%;
        margin-bottom: 24px;
        border: 1px solid #444;
      ">
        <h3 style="color: #aaa; margin-bottom: 16px; font-size: 0.95rem;">📊 あなたの記録</h3>
        
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; text-align: center;">
          <div>
            <div style="color: #888; font-size: 0.7rem;">最終順位</div>
            <div style="color: #fff; font-size: 1.5rem; font-weight: bold;">
              #${finalRank}
              <span style="font-size: 0.7rem; color: #666;">/ ${totalPlayers}</span>
            </div>
          </div>
          <div>
            <div style="color: #888; font-size: 0.7rem;">勝敗</div>
            <div style="font-size: 1.2rem;">
              <span style="color: #2ecc71;">${wins}W</span>
              <span style="color: #666;"> - </span>
              <span style="color: #e74c3c;">${losses}L</span>
            </div>
          </div>
          <div>
            <div style="color: #888; font-size: 0.7rem;">ファン</div>
            <div style="color: #ffd700; font-size: 1.5rem; font-weight: bold;">
              ★ ${player.fans}
            </div>
          </div>
        </div>
        
        <!-- 対戦履歴 -->
        <div style="margin-top: 16px; border-top: 1px solid #333; padding-top: 12px;">
          <div style="color: #666; font-size: 0.75rem; margin-bottom: 8px;">対戦履歴</div>
          <div style="display: flex; gap: 4px; justify-content: center; flex-wrap: wrap;">
            ${player.matchHistory.map((m) => `
              <span style="
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 4px;
                font-size: 0.7rem;
                background: ${m.result === 'win' ? 'rgba(46,204,113,0.2)' : 'rgba(231,76,60,0.2)'};
                color: ${m.result === 'win' ? '#2ecc71' : '#e74c3c'};
              ">
                ${m.result === 'win' ? 'W' : 'L'}
              </span>
            `).join('')}
          </div>
        </div>
      </div>

      <!-- アクションボタン -->
      <div style="display: flex; gap: 12px; flex-wrap: wrap; justify-content: center;">
        <button id="btnSpectate" class="btn" style="
          background: transparent;
          border: 2px solid #00fff5;
          color: #00fff5;
          padding: 12px 24px;
          min-height: 48px;
        ">👁️ 観戦する</button>
        
        <button id="btnExit" class="btn" style="
          background: transparent;
          border: 2px solid #666;
          color: #888;
          padding: 12px 24px;
          min-height: 48px;
        ">🚪 ロビーへ戻る</button>
      </div>
    </div>
  `);

  document.getElementById('btnSpectate')?.addEventListener('click', callbacks.onSpectate);
  document.getElementById('btnExit')?.addEventListener('click', callbacks.onExit);
}

// 観戦画面
export function renderSpectatorView(
  game: GameManager,
  currentRound: number,
  callbacks: { onExit: () => void }
): void {
  const ranking = game.getRanking();
  const activeCount = ranking.filter((r) => !r.player.isEliminated).length;
  const phase = game.getState().phase;

  render(`
    <div class="spectator-view" style="
      padding: 16px;
      max-width: 600px;
      margin: 0 auto;
    ">
      <div style="
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding: 10px 14px;
        background: rgba(0,0,0,0.3);
        border-radius: 8px;
      ">
        <div>
          <span style="color: #00fff5;">👁️ 観戦モード</span>
          <span style="color: #666; margin-left: 8px;">Round ${currentRound}</span>
        </div>
        <button id="btnExitSpectate" style="
          background: transparent;
          border: 1px solid #666;
          color: #888;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 0.8rem;
          cursor: pointer;
          min-height: 32px;
        ">退出</button>
      </div>

      <div style="
        background: rgba(0,0,0,0.3);
        border-radius: 12px;
        padding: 16px;
        margin-bottom: 16px;
      ">
        <h3 style="color: #ffd700; margin-bottom: 12px; font-size: 1rem;">
          🏆 ライブランキング
          <span style="font-size: 0.75rem; color: #666; margin-left: 8px;">
            残り${activeCount}人
          </span>
        </h3>
        
        ${ranking.slice(0, 8).map((r, i) => {
          const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : '';
          const isEliminated = r.player.isEliminated;
          const wins = r.player.matchHistory.filter(m => m.result === 'win').length;
          
          return `
            <div style="
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 8px 10px;
              margin: 4px 0;
              background: ${i < 2 ? 'rgba(255,215,0,0.1)' : 'rgba(255,255,255,0.03)'};
              border-radius: 6px;
              ${isEliminated ? 'opacity: 0.4;' : ''}
            ">
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="width: 24px; font-size: 0.9rem;">${medal || `#${r.rank}`}</span>
                <span>${r.player.info.type === 'bot' ? '🤖' : '👤'}</span>
                <span style="font-size: 0.85rem;">${r.player.info.name}</span>
                ${isEliminated ? '<span style="color: #888; font-size: 0.65rem;">敗退</span>' : ''}
              </div>
              <div style="text-align: right;">
                <span style="color: #ffd700; font-size: 0.9rem;">★ ${r.player.fans}</span>
                <div style="color: #666; font-size: 0.65rem;">${wins}勝</div>
              </div>
            </div>
          `;
        }).join('')}
      </div>

      <div style="
        text-align: center;
        color: #666;
        font-size: 0.85rem;
        padding: 12px;
        background: rgba(0,0,0,0.2);
        border-radius: 8px;
      ">
        ${phase === 'deckPhase' ? '🃏 デッキ構築中...' : 
          phase === 'matchPhase' ? '⚔️ バトル中...' :
          phase === 'finals' ? '🏆 決勝戦!' :
          '待機中...'}
      </div>
    </div>
  `);

  document.getElementById('btnExitSpectate')?.addEventListener('click', callbacks.onExit);
}
